import { useId, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/components/ui';
import { useRouteAnimation } from '@/contexts/AnimationContext';
import btcLogo from '@/features/finance/resources/images/btc.svg';
import ethLogo from '@/features/finance/resources/images/eth.svg';
import usdtLogo from '@/features/finance/resources/images/usdt.svg';
import ltcLogo from '@/features/finance/resources/images/ltc.svg';
import dogeLogo from '@/features/finance/resources/images/doge.svg';
import cashAppLogo from '@/features/finance/resources/images/cash-app.svg';
import chimeLogo from '@/features/finance/resources/images/chime.svg';
import applePayLogo from '@/features/finance/resources/images/apple-pay.svg';
import googlePayLogo from '@/features/finance/resources/images/google-pay.svg';

/**
 * The ways a player can pay (owner: crypto, Cash App, Chime, Apple Pay, Google Pay). Order is the
 * strip's order. Only what the deposit flow takes: no USD Coin (launch item A5).
 */
export const PAYMENT_METHODS = [
  { id: 'bitcoin', logo: btcLogo },
  { id: 'ethereum', logo: ethLogo },
  { id: 'tether', logo: usdtLogo },
  { id: 'litecoin', logo: ltcLogo },
  { id: 'dogecoin', logo: dogeLogo },
  { id: 'cashApp', logo: cashAppLogo },
  { id: 'chime', logo: chimeLogo },
  { id: 'applePay', logo: applePayLogo },
  { id: 'googlePay', logo: googlePayLogo },
] as const;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(REDUCED_MOTION_QUERY);
  mql.addEventListener?.('change', onChange);
  return () => mql.removeEventListener?.('change', onChange);
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * Equal-width items: a 56px tile with a 40px logo, the name under it. Every logo file is a square
 * that fills its box (coins are circles, wallets are rounded squares), so all nine show at the same
 * size. Moving, the loop needs one copy of the list (9 x 8rem + 9 x 1rem gap = 81rem) at least as
 * wide as the widest page column (PageShell wide: 80rem minus gutters), so the items keep that width
 * on every layout. Static, the items are narrower so a phone column (343 design px) holds four per
 * row.
 */
const itemClass = 'flex shrink-0 flex-col items-center gap-2';
const movingItemWidth = 'w-32';
const staticItemWidth = 'w-20';
const tileClass = 'flex size-14 items-center justify-center rounded-control border k-card bg-raised';

/**
 * "Pay your way": the payment logos at the end of the Play page. It slowly moves sideways (CSS
 * only: `.payments-marquee__track` in globals.css, the list twice, -50% per loop) and never stops
 * for the pointer: it takes no hover, click or drag. It is a static wrapped list when the player
 * turned animations off for the page or the system asks for reduced motion. Information only: no
 * buttons or links, so kiosks show it too.
 */
export default function PaymentMethodsStrip() {
  const { t } = useTranslation();
  const headingId = useId();
  const animationsOn = useRouteAnimation('home.paymentsMarquee');
  const reduceMotion = useSyncExternalStore(subscribeReducedMotion, prefersReducedMotion, () => false);
  const moving = animationsOn && !reduceMotion;

  const items = (copy: 'visible' | 'duplicate') =>
    PAYMENT_METHODS.map((method) => (
      <li key={`${copy}-${method.id}`} className={cx(itemClass, moving ? movingItemWidth : staticItemWidth)}>
        <span className={tileClass}>
          <img src={method.logo} alt="" width={40} height={40} draggable={false} className="size-10 object-contain" />
        </span>
        <span className="w-full text-center font-ui text-small text-secondary">
          {t(`payments.methods.${method.id}`)}
        </span>
      </li>
    ));

  return (
    <section aria-labelledby={headingId} className="flex min-w-0 flex-col gap-3">
      <h2 id={headingId} className="m-0 font-display text-title font-semibold text-primary">
        {t('payments.title')}
      </h2>
      {moving ? (
        <div className="payments-marquee">
          <div className="payments-marquee__track">
            <ul className="m-0 flex list-none gap-4 p-0 pr-4">{items('visible')}</ul>
            <ul aria-hidden="true" className="m-0 flex list-none gap-4 p-0 pr-4">
              {items('duplicate')}
            </ul>
          </div>
        </div>
      ) : (
        <ul className="m-0 flex list-none flex-wrap justify-center gap-x-1 gap-y-4 p-0">{items('visible')}</ul>
      )}
    </section>
  );
}
